/**
 * ModerationTextContent Component
 *
 * Displays multiple text messages in a vertical stack layout.
 * Each message is shown in its own container with an auto-generated label.
 *
 * @example
 * ```tsx
 * <ModerationTextContent
 *   messages={[
 *     { id: 'msg1', content: 'First message text' },
 *     { id: 'msg2', content: 'Second message text' }
 *   ]}
 * />
 * ```
 */

import { Text, ListItem, Icon, IconSize, Chat } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';

import * as styles from './ModerationCaseCard.css';

/**
 * Text message interface
 */
export interface TextMessage {
  /**
   * Unique identifier for the message
   */
  id: string;

  /**
   * Text content of the message
   */
  content: string;

  /**
   * Optional custom label (defaults to "Message N")
   */
  label?: string;

  /**
   * Optional timestamp for the message
   */
  timestamp?: string;
}

/**
 * Props for ModerationTextContent
 */
export interface ModerationTextContentProps {
  /**
   * Array of text messages to display
   */
  messages: TextMessage[];

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * ModerationTextContent Component
 *
 * Renders multiple text messages in individual containers with labels.
 * Supports auto-labeling (Message 1, Message 2, etc.) and custom labels.
 */
export function ModerationTextContent({ messages, className }: ModerationTextContentProps): ReactNode {
  if (!messages || messages.length === 0) {
    return (
      <Text color="secondary" size="sm">
        No text content available
      </Text>
    );
  }

  const combinedClassName = [styles.textContentContainer, className].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName}>
      {messages.map((message, index) => {
        // Format content as plain string with timestamp appended if present
        const contentText = message.timestamp ? `${message.content}\n\n${message.timestamp}` : message.content;

        return (
          <ListItem
            key={message.id}
            as="button"
            background="visible"
            label1={contentText}
            label2={message.label || `Message ${index + 1}`}
            leadingIcon={<Icon icon={Chat} iconSize={IconSize.Small} />}
            type="button"
          />
        );
      })}
    </div>
  );
}

ModerationTextContent.displayName = 'ModerationTextContent';
